import {
  devicesMetrics,
  getDeviceCommonName,
  isModelVirtualWeatherStation,
  isVirtualWeatherStation
} from '@weenat/client/dist/core/devices'
import isIrrigationDevice from '@weenat/client/dist/core/devices/isIrrigationDevice'
import { preferredMetricsOrder } from '@weenat/client/dist/core/metrics'
import { Brand, getLastUpdateThresholds } from '@weenat/client/dist/enums/Devices'
import { isMetricVisibleOnWeatherMapSummary } from '@weenat/client/dist/enums/MetricIds'
import { Device } from '@weenat/client/dist/resources/devices'
import { useIntl } from '@weenat/wintl'
import { Head } from 'app/routx-router'
import IrrigationDeviceDashboardEmptyState from 'app/src/devices/IrrigationDeviceDashboardEmptyState'
import withClientDevice from 'app/src/hocs/withClientDevice'
import NoEmissionEmptyState from 'app/src/kit/empty-state/NoEmissionEmptyState'
import Grid from 'app/src/kit/Grid'
import LastUpdateBadge from 'app/src/kit/LastUpdateBadge'
import Text from 'app/src/kit/Text'
import { UpdateInformationTiming } from 'app/src/kit/UpdateInformation'
import MetricWeedget, { MetricWeedgetCondensed } from 'app/src/kit/weedget/MetricWeedget'
import { useToggleFeature } from 'app/state'
import logEvent from 'app/utils/analytics'
import { isNil } from 'lodash-es'
import { useEffect, useMemo } from 'react'

const DeviceScreen: FC<{ device: Device }> = ({ device }) => {
  const [{ marker_redesign, smv }] = useToggleFeature()
  const commonName = getDeviceCommonName(device)

  const { t } = useIntl()

  const visibleMetrics = device.availableMeasures.filter(
    (metric) => isMetricVisibleOnWeatherMapSummary(metric) || metric === 'LW_D'
  )

  const sortedVisibleMetrics = useMemo(
    () =>
      visibleMetrics.sort(
        (a, b) => preferredMetricsOrder.indexOf(a) - preferredMetricsOrder.indexOf(b)
      ),
    [visibleMetrics]
  )

  const metricsToDisplay = useMemo(() => {
    return device.availableMeasures.filter((metric) => devicesMetrics.includes(metric))
  }, [device.availableMeasures])

  const lastUpdateThresholds = getLastUpdateThresholds(
    device.brand,
    undefined,
    smv ? isVirtualWeatherStation(device) : false
  )

  const showIrrigationEmptyState = isIrrigationDevice(device) && device?.brand !== Brand.corhize

  useEffect(() => {
    logEvent('device_summary_page')
  }, [])

  return (
    <>
      <Head title={commonName} />

      {isNil(device.lastMeasuredAt) ? (
        <Flex
          $height={'100%'}
          $flexDirection='column'
          $alignItems='center'
          $p='lg'
          $gap='xl'
          $mx='auto'
          $maxWidth={1280}
        >
          <NoEmissionEmptyState
            IconName={
              isModelVirtualWeatherStation(device.model)
                ? 'SingleVirtualSensorFilled'
                : 'SingleSensorFilled'
            }
            title={t('weedget.not_emitted_yet.wait_message')}
            description={t('weedget.not_emitted_yet.waiting_for_sensor')}
            metrics={metricsToDisplay}
          />
        </Flex>
      ) : (
        <Flex $flexDirection='column' $p='lg' $gap='xl' $mx='auto' $maxWidth={1280}>
          {showIrrigationEmptyState ? (
            <IrrigationDeviceDashboardEmptyState device={device} />
          ) : (
            <>
              <Flex $flexDirection='column' $gap='md'>
                <Flex $gap='sm'>
                  <Text $fontWeight='bold'>
                    {t(smv ? 'map.universes.aggregate.last-measured' : 'periods.latest', {
                      capitalize: true
                    })}
                  </Text>
                  <Flex $alignItems='center' $gap='xs'>
                    {marker_redesign ? (
                      <LastUpdateBadge
                        timestamp={parseInt(device.lastMeasuredAt, 10)}
                        timezone={device.timezone}
                        warningThreshold={lastUpdateThresholds.warningThreshold}
                        dangerThreshold={lastUpdateThresholds.dangerThreshold}
                      />
                    ) : (
                      <UpdateInformationTiming timestamp={parseInt(device.lastMeasuredAt, 10)} />
                    )}
                  </Flex>
                </Flex>
                <Grid $templateColumns='1fr 1fr 1fr' $gap='md'>
                  {sortedVisibleMetrics.map((metric) => (
                    <MetricWeedgetCondensed key={metric} metric={metric} />
                  ))}
                </Grid>
              </Flex>
              <Flex $flexDirection='column' $gap='md'>
                <Text $fontWeight='bold'>{t('periods.recently', { capitalize: true })}</Text>
                <Flex $flexDirection='column' $gap='md'>
                  {sortedVisibleMetrics.map((metric) => (
                    <Flex key={metric} $height={280}>
                      <MetricWeedget metric={metric} />
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
      )}
    </>
  )
}

export const Component = withClientDevice()(DeviceScreen)
export default Component
